/**
 * Utility functions for processing image codes in content
 */

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://dev.oxygentamil.com/api"

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/**
 * Strip tags / entities, then find [12-digit] placeholders.
 * Handles rich-text HTML that splits brackets across tags, e.g.
 * <span>[</span><span>231926196737</span><span>]</span>
 */
export function extractImageCodesFromHtml(html: string): string[] {
  if (!html) return []
  let plain = html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
  plain = plain.replace(/<[^>]+>/g, " ")
  plain = plain
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/gi, " ")
    .replace(/&#xA0;/gi, " ")
    .replace(/\s+/gu, " ")
    .trim()
  const codes = new Set<string>()
  const re = /\[\s*(\d{12})\s*\]/gu
  let m: RegExpExecArray | null
  while ((m = re.exec(plain)) !== null) {
    codes.add(m[1])
  }
  return [...codes]
}

/**
 * Replace one image code in raw HTML with replacement (e.g. <img>).
 * Tries split-span patterns first, then literal [code].
 */
export function replaceImageCodeInHtml(html: string, code: string, replacement: string): string {
  const esc = escapeRegex(code)
  const splitSpan = new RegExp(
    `<span[^>]*>\\s*\\[\\s*</span>\\s*(?:<br\\s*/?>\\s*)?<span[^>]*>\\s*${esc}\\s*</span>\\s*(?:<br\\s*/?>\\s*)?<span[^>]*>\\s*\\]\\s*</span>`,
    "gi",
  )
  const literal = new RegExp(`\\[\\s*${esc}\\s*\\]`, "g")
  let out = html.replace(splitSpan, replacement)
  out = out.replace(literal, replacement)
  return out
}

/**
 * Replace [code] patterns in HTML content with image tags
 * This processes the content asynchronously to fetch image URLs
 */
export async function replaceImageCodesWithImages(htmlContent: string): Promise<string> {
  const codes = extractImageCodesFromHtml(htmlContent)
  if (codes.length === 0) {
    return htmlContent
  }

  const codePromises = codes.map(async (code) => {
    try {
      const response = await fetch(`${API_BASE}/upload/image-code/${code}`)
      if (response.ok) {
        const data = await response.json()
        const url = data.url?.startsWith("http") ? data.url : `${API_BASE.replace("/api", "")}${data.url}`
        return { code, url, title: data.title || "", link: data.link || "", alt: data.alt || "" }
      }
    } catch (error) {
      console.error(`Failed to fetch image for code ${code}:`, error)
    }
    return { code, url: null, title: "", link: "", alt: "" }
  })

  const imageData = await Promise.all(codePromises)
  const imageMap = new Map(imageData.map((item) => [item.code, item]))

  let processedContent = htmlContent
  for (const code of codes) {
    const data = imageMap.get(code)
    if (data?.url) {
      const alt = data.alt?.trim() || `Image ${code}`
      const title = data.title?.trim() || ""
      const link = data.link?.trim() || ""
      const imgTag = `<img src="${data.url}" alt="${alt.replace(/"/g, "&quot;")}"${title ? ` title="${title.replace(/"/g, "&quot;")}"` : ""} style="max-width: 100%; height: auto; display: block; margin: 1rem 0;" />`
      const finalTag = link
        ? `<a href="${link.replace(/"/g, "&quot;")}"${title ? ` title="${title.replace(/"/g, "&quot;")}"` : ""}>${imgTag}</a>`
        : imgTag
      processedContent = replaceImageCodeInHtml(processedContent, code, finalTag)
    }
  }

  return processedContent
}

/**
 * Replace [code] patterns in HTML content with placeholders
 */
export function replaceImageCodesWithPlaceholders(htmlContent: string): string {
  const codes = extractImageCodesFromHtml(htmlContent)
  let out = htmlContent
  for (const code of codes) {
    const ph = `<span data-image-code="${code}" class="image-code-placeholder">[Image ${code}]</span>`
    out = replaceImageCodeInHtml(out, code, ph)
  }
  return out
}

export interface ImageCodeFormEntry {
  code: string
  url: string
  title?: string
  alt?: string
}

function escapeHtmlAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;")
}

/**
 * Replace [12-digit-code] in HTML using editor form rows only (no API).
 * Relative image URLs are resolved with absoluteOrigin (e.g. site origin without /api).
 */
export function replaceImageCodesFromForm(
  htmlContent: string,
  entries: ImageCodeFormEntry[],
  absoluteOrigin?: string
): string {
  const origin = (absoluteOrigin || "").replace(/\/$/, "")
  const map = new Map(entries.map((e) => [e.code, e]))
  const codes = extractImageCodesFromHtml(htmlContent)
  let out = htmlContent
  for (const code of codes) {
    const item = map.get(code)
    if (!item?.url?.trim()) {
      const missing = `<span style="display:inline-block;margin:0.5rem 0;padding:0.35rem 0.6rem;border:1px dashed #94a3b8;border-radius:6px;font-size:0.75rem;color:#64748b;">Image [${escapeHtmlAttr(code)}] - add it under Input Image in the sidebar</span>`
      out = replaceImageCodeInHtml(out, code, missing)
      continue
    }
    let src = item.url.trim()
    if (!/^https?:\/\//i.test(src) && origin) {
      src = `${origin}${src.startsWith("/") ? "" : "/"}${src.replace(/^\//, "")}`
    }
    const alt = escapeHtmlAttr((item.alt || "").trim() || `Image ${code}`)
    const titlePart = (item.title || "").trim()
    const titleAttr = titlePart ? ` title="${escapeHtmlAttr(titlePart)}"` : ""
    const img = `<img src="${escapeHtmlAttr(src)}" alt="${alt}"${titleAttr} style="max-width:100%;height:auto;display:block;margin:1rem 0;" />`
    out = replaceImageCodeInHtml(out, code, img)
  }
  return out
}
